// شهرهایی که اپ داره، و درخواست برای شهری که نداره (مایگریشن 0063).
//
// مرزش با geoRepo روشنه: `geoRepo` فهرستِ **دنیا**ست (۱۵۳ هزار شهر، فقط برای
// انتخاب کردن)، این یکی فهرستِ **ما**ست — همون چند شهری که کرالر براشون گشته
// و فهرستِ کشف توشون چیزی برای نشون دادن داره.
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import type { AppCity, UserId } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** همون قاعده‌ی geoRepo: بدونِ بک‌اند، فرم نباید خالی بمونه. */
const offline = () => !isSupabaseConfigured() || isDemoMode;

/**
 * شهرهای دمو. اتاوا اولِ فهرسته چون همون شهریه که داده‌ی دمو توشه.
 * مختصات واقعی‌ان تا «نزدیک‌ترین شهر» توی دمو هم جوابِ درست بده.
 */
const DEMO_APP_CITIES: AppCity[] = [
  { id: 1, name: 'Ottawa', countryCode: 'CA', region: 'Ontario', lat: 45.4215, lng: -75.6972, countryName: 'Canada' },
  { id: 2, name: 'Toronto', countryCode: 'CA', region: 'Ontario', lat: 43.7001, lng: -79.4163, countryName: 'Canada' },
  { id: 3, name: 'Montreal', countryCode: 'CA', region: 'Quebec', lat: 45.5088, lng: -73.5878, countryName: 'Canada' },
];

const mapAppCity = (row: any): AppCity => ({
  id: Number(row.city?.id ?? row.city_id),
  name: row.city?.name ?? '',
  countryCode: row.city?.country_code ?? '',
  region: row.city?.region ?? null,
  lat: row.city?.lat ?? null,
  lng: row.city?.lng ?? null,
  countryName: row.city?.country?.name ?? null,
});

export const cityRepo = {
  /**
   * شهرهای فعالِ اپ که رخدادِ زنده دارند، الفبایی.
   *
   * چند تان، نه چند هزار — پس یک‌جا می‌آن و فیلترشون سمتِ خودِ دستگاه انجام
   * می‌شه. (برعکسِ `geoRepo.cities` که هیچ‌وقت کامل دانلود نمی‌شه.)
   *
   * شهرِ بی‌رخداد بیرون می‌مونه (WHOSUP-252، مایگریشن 0151): انتخابش فقط یه
   * صفحه‌ی کشفِ خالی می‌داد. ردیفش توی `app_cities` می‌مونه — کرالر از همون
   * می‌فهمه کجا باید دنبالِ رخداد بگرده — فقط این‌جا نشون داده نمی‌شه.
   */
  async list(): Promise<AppCity[]> {
    if (offline()) return DEMO_APP_CITIES;
    const [rows, live] = await Promise.all([
      supabase
        .from('app_cities')
        .select('city_id, city:cities(id, name, country_code, region, lat, lng,'
          + ' country:countries(name))')
        .eq('is_active', true),
      supabase.rpc('app_cities_with_events'),
    ]);
    if (rows.error) throw rows.error;
    if (live.error) throw live.error;
    const withEvents = new Set(((live.data ?? []) as unknown[]).map(Number));
    return (rows.data ?? []).map(mapAppCity)
      .filter((c) => !!c.name && withEvents.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * یه شهرِ مشخص از فهرستِ **دنیا** — نه فقط شهرهای اپ.
   *
   * شهرِ دیفالتِ کاربر می‌تونه شهری باشه که هنوز محتوا نداره (دقیقاً همون
   * کسیه که دکمه‌ی «این شهر رو اضافه کنید» رو می‌زنه)، پس این‌جا نباید به
   * `app_cities` محدود شه.
   */
  async byId(id: number): Promise<AppCity | null> {
    if (offline()) return DEMO_APP_CITIES.find((c) => c.id === id) ?? null;
    const { data, error } = await supabase
      .from('cities')
      .select('id, name, country_code, region, lat, lng, country:countries(name)')
      .eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapAppCity({ city: data }) : null;
  },

  /**
   * «شهرِ من اینجا نیست.»
   *
   * ردیفِ تکراری خطا نیست: ایندکسِ یکتای (شهر، کاربر) یعنی درخواستِ دوباره‌ی
   * همون شهر توسطِ همون آدم به همون یه ردیف می‌خوره — که دقیقاً همون چیزیه
   * که می‌خوایم. کاربر نباید برای «قبلاً خواستی» پیامِ خطا ببینه.
   */
  async request(cityId: number, uid?: UserId | null): Promise<void> {
    if (offline()) return;
    const { error } = await supabase
      .from('city_requests').insert({ city_id: cityId, requested_by: uid ?? null });
    if (error && error.code !== '23505') throw error;
  },
};
